/**
 * Chamfer corner-triangle drags: two-axis, setting a chamfer's dx and dy
 * together from the pointer.
 */

import { clamp, freeElementRadians, Point } from '@kaiizen/kai-floor-plan';
import { chamferData } from '../editorGeometry';
import type { EditorMutableState, Gesture, Selection } from '../editorState';
import { select } from '../selection';
import { applyChamfer, type ChamferSelection, isChamferSelection } from '../valueTargets';
import { beginDraft } from './draft';

type TriangleGesture = Extract<Gesture, { kind: 'tri' }>;

/** Rotate a point about (centerX, centerY) by `radians`. */
function rotateAbout(point: Point, centerX: number, centerY: number, radians: number): Point {
  const cos = Math.cos(radians), sin = Math.sin(radians);
  return {
    x: centerX + (point.x - centerX) * cos - (point.y - centerY) * sin,
    y: centerY + (point.x - centerX) * sin + (point.y - centerY) * cos,
  };
}

/**
 * The frame a rotated free element's chamfer math runs in: its center and
 * rotation, so each move can inverse-rotate the pointer into the element's
 * local, unrotated frame. Undefined for every other chamfer.
 */
function localFrame(state: EditorMutableState, selection: ChamferSelection): TriangleGesture['local'] {
  if (selection.kind !== 'innerCorner') return undefined;
  const element = state.shape.freeElements?.[selection.index];
  const radians = element ? freeElementRadians(element) : 0;
  return element && radians ? { cx: element.x + element.width / 2, cy: element.y + element.length / 2, rad: radians } : undefined;
}

/** Drag a chamfer's corner triangle (2-axis: sets dx and dy together). */
export function beginTriDrag(state: EditorMutableState, selection: Selection): boolean {
  if (!isChamferSelection(selection)) return false;
  select(state, selection);
  const chamfer = chamferData(state, selection);
  if (!chamfer) return false;
  beginDraft(state);
  state.drag = {
    kind: 'tri',
    sx: chamfer.sx,
    sy: chamfer.sy,
    cx: chamfer.corner.x,
    cy: chamfer.corner.y,
    maxDx: chamfer.maxDx,
    maxDy: chamfer.maxDy,
    local: localFrame(state, selection),
  };
  return true;
}

/** Advance a corner-triangle drag: both legs follow the pointer, clamped to the corner's limits. */
export function moveChamferTriangle(state: EditorMutableState, gesture: TriangleGesture, model: Point): void {
  if (!isChamferSelection(state.selection)) return;
  const local = gesture.local;
  const pointer = local ? rotateAbout(model, local.cx, local.cy, -local.rad) : model;
  state.draftShape = applyChamfer(
    state.shape,
    state.selection,
    clamp(gesture.sx * (pointer.x - gesture.cx), 0, gesture.maxDx),
    clamp(gesture.sy * (pointer.y - gesture.cy), 0, gesture.maxDy),
  );
}
